import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import lux from "@/assets/1742561569_3593189334595606656.webp";
import artisan from "@/assets/1780018027_3907396849912522036.webp";
import decor from "@/assets/1779947897_3906808566430270185.webp";
import romantic from "@/assets/1779847500_3905966368420069795.webp";

export const Route = createFileRoute("/collections")({
  head: () => ({
    meta: [
      { title: "All Collections — Royal Lumina" },
      { name: "description", content: "Discover every Royal Lumina candle collection — luxury, artisan, decor, romantic, aroma and gift sets." },
      { property: "og:title", content: "All Collections — Royal Lumina" },
      { property: "og:description", content: "Every Royal Lumina collection in one place." },
    ],
  }),
  component: CollectionsPage,
});

type CollectionSearch = { category?: "Luxury" | "Artisan" | "Decorative" | "Aroma" | "Gift Sets"; color?: "rose" };
const collections: { name: string; tagline: string; count: number; img: string; tone: string; search: CollectionSearch }[] = [
  { name: "Luxury Collection", tagline: "Golden hour, poured.", count: 10, img: lux, tone: "Amber · Oud · Saffron", search: { category: "Luxury" } },
  { name: "Artisan Collection", tagline: "Sculpted by hand, blessed by fire.", count: 12, img: artisan, tone: "Sandalwood · Rose · Musk", search: { category: "Artisan" } },
  { name: "Decor Collection", tagline: "Objects that live between rituals.", count: 15, img: decor, tone: "Fig · Cedar · Bergamot", search: { category: "Decorative" } },
  { name: "Romantic Collection", tagline: "For two, for one, for the light.", count: 18, img: romantic, tone: "Rose · Vanilla · Amber", search: { color: "rose" } },
  { name: "Aroma Collection", tagline: "Wellness that lingers in the room.", count: 14, img: lux, tone: "Lavender · Eucalyptus · Neroli", search: { category: "Aroma" } },
  { name: "Gift Sets", tagline: "Rituals, wrapped in silk.", count: 8, img: artisan, tone: "Curated · Ribboned · Signed", search: { category: "Gift Sets" } },
];

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { API_BASE_URL } from "@/lib/api";

function CollectionsPage() {
  const [dbCollections, setDbCollections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getCollections = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/collections`);
        if (res.ok) {
          const data = await res.json();
          setDbCollections(data);
        } else {
          setDbCollections(collections);
        }
      } catch (err) {
        setDbCollections(collections);
      } finally {
        setLoading(false);
      }
    };
    getCollections();
  }, []);

  const resolveImg = (imgUrl: string | undefined, fallback: string) => {
    if (!imgUrl) return fallback;
    if (imgUrl.includes("1742561569")) return lux;
    if (imgUrl.includes("1780018027")) return artisan;
    if (imgUrl.includes("1779947897")) return decor;
    if (imgUrl.includes("1779847500")) return romantic;
    if (imgUrl.startsWith("http://") || imgUrl.startsWith("https://") || imgUrl.startsWith("/assets/")) return imgUrl;
    return fallback;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AnnouncementBar />
      <Navbar />

      <section className="relative bg-primary text-cream -mt-20 pt-32 pb-16">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_30%,var(--gold)_0%,transparent_45%)]" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-gold/80">Home · Collections</div>
          <h1 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl">
            The <span className="italic text-gold">Collections</span>
          </h1>
          <p className="mt-4 max-w-xl text-cream/70">Six worlds, hand-poured — each one a different way to keep the light.</p>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        {loading ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {dbCollections.map((c, idx) => {
              const search = c.search || { collection: c.slug };
              const count = c._count?.products !== undefined ? c._count.products : (c.count || 0);
              const fallbacks = [lux, artisan, decor, romantic];
              const resolvedImage = resolveImg(c.img, fallbacks[idx % fallbacks.length]);

              return (
                <Link
                  key={c.name}
                  to="/shop"
                  search={search}
                  className="group relative overflow-hidden rounded-[24px] shadow-luxe hover:shadow-luxe-hover transition-all duration-500 aspect-[4/5]"
                >
                  <img
                    src={resolvedImage}
                    alt={c.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = fallbacks[idx % fallbacks.length];
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-gold/0 group-hover:ring-gold/60 transition-all duration-500 rounded-[24px]" />
                  <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-10 text-cream">
                    <div className="text-[0.6rem] tracking-[0.3em] uppercase text-gold/90">{count} pieces {c.tone ? `· ${c.tone}` : ""}</div>
                    <h3 className="mt-2 font-display text-3xl lg:text-4xl">{c.name}</h3>
                    <p className="mt-2 text-cream/70 max-w-sm">{c.tagline}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm text-gold group-hover:gap-3 transition-all">
                      Explore <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
